import { t } from 'i18next';
import { useMemo, useState } from 'react';
import { useSnackbar, closeSnackbar } from 'notistack';

import { Table, TableBody, TableContainer } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { PageOptions } from 'src/services/api.types';
import { JobVerificationStatus } from 'src/services/jobs/jobs.types';
import { ReferenceVisibilityStatus } from 'src/services/references/references.types';
import { useGetApplicationByIdQuery } from 'src/services/applications/applications.service';
import { useUpdateVisibilityMutation, useGetReferencesByApplicationQuery } from 'src/services/references/references.service';

import Scrollbar from 'src/components/scrollbar';
import {
  useTable,
  emptyRows,
  TableNoData,
  TableEmptyRows,
  TableHeadCustom
} from 'src/components/table';

import ViewApplicationReferenceModal from 'src/sections/jobs/applications/modals/references/view-submissions-modal';
import ApplicationReferencesTableRow from 'src/sections/jobs/applications/detail/application-references-table/application-references-table-row';

import { TenantType } from 'src/types/enums';

// ----------------------------------------------------------------------

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 5,
};

type Props = {
  applicationId?: string;
}

// ----------------------------------------------------------------------\

export default function ApplicationReferencesTable({ applicationId }: Props) {

  const table = useTable();

  const tenantType = useOrgTenant();

  const { enqueueSnackbar } = useSnackbar();

  const [pageState, setPageState] = useState<PageOptions>(DEFAULT_PAGE);

  const viewSubmissionsControl = useBoolean();

  const [selectedReferenceId, setSelectedReferenceId] = useState<string | null>(null);

  const denseHeight = table.dense ? 56 : 56 + 20;

  const { currentData: applicationReferences } = useGetReferencesByApplicationQuery({
    applicationId: applicationId as string,
    params: {
      ...pageState
    },
  }, {
    skip: !applicationId
  });

  const { currentData: application } = useGetApplicationByIdQuery(applicationId as string, {
    skip: !applicationId
  });

  const shouldShowVisibilityStatus = useMemo(() => {
    if (!application) return false;
    return tenantType === TenantType.Recruiter && application?.job.verification_status === JobVerificationStatus.VERIFIED;
  }, [tenantType, application]);

  const TABLE_HEAD = useMemo(() => [
    { id: 'name', label: t('profile.referees.table.labels.name'), width: 200 },
    { id: 'email', label: t('profile.referees.table.labels.email') },
    { id: 'phone', label: t('profile.referees.table.labels.phone') },
    { id: 'company', label: t('profile.referees.table.labels.company_name') },
    ...shouldShowVisibilityStatus ? [{
      id: 'visibility', label: t('profile.referees.table.labels.visibility_status')
    }] : [],
    { id: 'actions', label: '' }
  ], [shouldShowVisibilityStatus]);

  const [updateReferenceVisibility] = useUpdateVisibilityMutation();

  const notFound = !applicationReferences || !applicationReferences.count;

  const handleViewSubmissions = (referenceId: string) => {
    setSelectedReferenceId(referenceId);
    viewSubmissionsControl.onTrue();
  }

  const handleCloseViewSubmissions = () => {
    setSelectedReferenceId(null);
    viewSubmissionsControl.onFalse();
  }

  const handleShareWithClient = async (referenceId: string) => {
    if (!applicationId) return;

    const toastId = enqueueSnackbar(t('applications.api.update_reference_visibility.loading'), {
      variant: 'loading',
      persist: true,
    });

    try {
      await updateReferenceVisibility({
        applicationId: applicationId as string,
        referenceId,
        data: {
          status: ReferenceVisibilityStatus.SHARED
        }
      }).unwrap();

      closeSnackbar(toastId);
      enqueueSnackbar(t('applications.api.update_reference_visibility.success'), { variant: 'success' });
    }
    catch (error) {
      console.error(error);
      closeSnackbar(toastId);
      enqueueSnackbar(t('applications.api.update_reference_visibility.default_error'), {
        variant: 'error'
      });
    }
  }

  return (
    <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
      <Scrollbar>
        <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
          <TableHeadCustom
            order={table.order}
            headLabel={TABLE_HEAD}
            rowCount={applicationReferences?.count}
            numSelected={table.selected.length}
          />

          <TableBody>
            {applicationReferences && applicationReferences.results?.map((row) => (
              <ApplicationReferencesTableRow
                key={row.id}
                row={row}
                showVisibilityStatus={shouldShowVisibilityStatus}
                onViewSubmissions={handleViewSubmissions}
                onShareWithClient={shouldShowVisibilityStatus ? handleShareWithClient : undefined}
              />
            ))}

            <TableEmptyRows
              height={denseHeight}
              emptyRows={emptyRows(table.page, table.rowsPerPage, applicationReferences?.count ?? 0)}
            />

            <TableNoData notFound={notFound} />
          </TableBody>
        </Table>
      </Scrollbar>

      {
        viewSubmissionsControl.value && (
          <ViewApplicationReferenceModal
            open={viewSubmissionsControl.value}
            applicationId={applicationId as string}
            referenceId={selectedReferenceId as string}
            onClose={handleCloseViewSubmissions}
          />
        )
      }
    </TableContainer>
  );
}
